import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppDispatch } from './useReduxTypeScript';
import {
  setLogoFromApi,
  removeLogo,
  resetBranding,
} from '../utils/redux/slices/brandingSlice';
import {
  getUserLogosApi,
  uploadLogoApi,
  updateLogoMetadataApi,
  replaceLogoFileApi,
  deleteLogoApi,
} from '../utils/apis';
import { useUser } from './useUser';

export const useBrandingLogo = () => {
  const { user } = useUser();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  // Fetch logos on mount
  const {
    data: logosData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['brandingLogo', user?._id],
    queryFn: getUserLogosApi,
    enabled: !!user?._id,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Sync with Redux when data is fetched or user changes
  useEffect(() => {
    if (logosData?.data?.data && logosData.data.data.length > 0) {
      // Use the first logo (most recent)
      const latestLogo = logosData.data.data[0];
      dispatch(setLogoFromApi(latestLogo));
    } else if (logosData?.data?.data && logosData.data.data.length === 0) {
      // No logos found for this user - reset to default
      dispatch(resetBranding());
    }
  }, [logosData, dispatch]);

  // Reset branding when user changes
  useEffect(() => {
    // When user changes, reset branding state
    return () => {
      // Cleanup function runs when user._id changes
      dispatch(resetBranding());
    };
  }, [user?._id, dispatch]);

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: uploadLogoApi,
    onSuccess: async data => {
      // Update Redux with new logo
      if (data?.data?.data) {
        dispatch(setLogoFromApi(data.data.data));
      }
      // Invalidate queries to refresh
      await queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      await refetch();
    },
  });

  // Update logo metadata mutation
  const updateLogoMetadataMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { userId: string; title: string; subtitle?: string };
    }) => updateLogoMetadataApi(id, data),
    onSuccess: async data => {
      if (data?.data?.data) {
        dispatch(setLogoFromApi(data.data.data));
      }
      // Invalidate and refetch immediately
      await queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      await refetch();
    },
  });

  // Replace logo file mutation
  const replaceLogoFileMutation = useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      replaceLogoFileApi(id, formData),
    onSuccess: async data => {
      if (data?.data?.data) {
        dispatch(setLogoFromApi(data.data.data));
      }
      await queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      await refetch();
    },
  });

  // Delete logo mutation
  const deleteLogoMutation = useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      deleteLogoApi(id, userId),
    onSuccess: async () => {
      dispatch(removeLogo());
      await queryClient.invalidateQueries({ queryKey: ['brandingLogo'] });
      await refetch();
    },
  });

  return {
    // Query states
    logos: logosData?.data?.data || [],
    isLoading,
    isError,
    error,

    // Mutation functions
    uploadLogo: uploadLogoMutation.mutate,
    uploadLogoAsync: uploadLogoMutation.mutateAsync,
    isUploadingLogo: uploadLogoMutation.isPending,
    uploadError: uploadLogoMutation.error,

    updateLogoMetadata: updateLogoMetadataMutation.mutate,
    updateLogoMetadataAsync: updateLogoMetadataMutation.mutateAsync,
    isUpdatingMetadata: updateLogoMetadataMutation.isPending,
    updateMetadataError: updateLogoMetadataMutation.error,

    replaceLogoFile: replaceLogoFileMutation.mutate,
    replaceLogoFileAsync: replaceLogoFileMutation.mutateAsync,
    isReplacingLogo: replaceLogoFileMutation.isPending,
    replaceLogoError: replaceLogoFileMutation.error,

    deleteLogo: deleteLogoMutation.mutate,
    deleteLogoAsync: deleteLogoMutation.mutateAsync,
    isDeletingLogo: deleteLogoMutation.isPending,
    deleteLogoError: deleteLogoMutation.error,
  };
};
